import React, { useEffect, useState, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target, Award, Activity, Package, FileText,
  ShieldCheck, AlertTriangle, Info, X, RotateCcw,
  Zap, Flame, Car
} from 'lucide-react';
import { SCOPE_COLORS, SCOPE_ICONS } from './constants';
import { assessTile, composeVerdict } from '../../utils/plausibilityVerdict';
import { useStaticData } from '../../hooks/useStaticData';
import CategoryCompletionList from './CategoryCompletionList';

const TrackingTab = ({ stats, summary, plausibilityResult, plausibilityLoading, runPlausibilityCheck, dismissPlausibilityAlert, restorePlausibilityAlert, formatEmissions, formatChartValue, toggleCategoryCompleted, togglingCode }) => {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  // ENH-04 Lot 3a : noms des catégories (lecture seule) pour la liste par scope.
  const { getCategoryByCode, fetchCategories } = useStaticData();
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const scopeNames = {
    scope1: t('dashboard.scopes.scope1'),
    scope2: t('dashboard.scopes.scope2'),
    scope3_amont: t('dashboard.scopes.scope3_amont'),
    scope3_aval: t('dashboard.scopes.scope3_aval'),
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Activity, color: 'blue', label: t('dashboard.tracking.activitiesEntered'), value: stats.totalActivities },
          { icon: Package, color: 'purple', label: t('dashboard.tracking.productsDefined'), value: stats.totalProducts },
          { icon: FileText, color: 'green', label: t('dashboard.tracking.categoriesFilled'), value: `${stats.completedCategories}/${stats.totalCategories}` },
          { icon: Target, color: 'amber', label: t('dashboard.tracking.progressShort'), value: `${stats.totalCategories > 0 ? Math.round((stats.completedCategories / stats.totalCategories) * 100) : 0}%` }
        ].map((card, i) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i }}
              className={`p-5 rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-white shadow-sm border border-gray-100'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl bg-${card.color}-500/20`}>
                  <Icon className={`w-6 h-6 text-${card.color}-500`} />
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{card.label}</p>
                  <p className="text-2xl font-bold">{card.value}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Scope Completion Cards */}
      <div>
        <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('dashboard.tracking.progressByScopeHeading')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {summary?.scope_completion && Object.entries(summary.scope_completion).map(([scope, data], index) => {
            const Icon = SCOPE_ICONS[scope] || Activity;
            const scopeEmissions = summary?.scope_emissions?.[scope] || 0;
            const emissions = formatEmissions(scopeEmissions);
            const completion = data.percentage || 0;
            const total = data.total_categories || 0;
            const pct = (n) => (total > 0 ? (n / total) * 100 : 0);

            return (
              <motion.div
                key={scope}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className={`p-5 rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-white shadow-sm border border-gray-100'}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${SCOPE_COLORS[scope]}/20`}>
                      <Icon className={`w-5 h-5 ${SCOPE_COLORS[scope].replace('bg-', 'text-')}`} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {scopeNames[scope]}
                      </h3>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {data.completed}/{data.total_categories} {t('dashboard.tracking.categoriesShort')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ENH-04bis : jauge BICOLORE — vert=terminé / gris=ouvert (= total - completed). */}
                <div className={`flex h-2 rounded-full overflow-hidden mb-2 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                  <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${pct(data.completed)}%` }} />
                  <div className={`h-full transition-all duration-500 ${isDark ? 'bg-slate-500' : 'bg-gray-300'}`} style={{ width: `${pct(total - data.completed)}%` }} />
                </div>
                <p className={`text-xs mb-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  {t('dashboard.completion.shortSummary')
                    .replace('{completed}', data.completed)
                    .replace('{total}', data.total_categories)}
                </p>

                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold">{emissions.value}</p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{emissions.unit}</p>
                  </div>
                  <div className={`text-right px-3 py-1 rounded-lg ${
                    completion === 100
                      ? 'bg-green-500/20 text-green-500'
                      : completion > 0
                        ? 'bg-amber-500/20 text-amber-500'
                        : isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <span className="text-lg font-bold">{completion}%</span>
                  </div>
                </div>

                <CategoryCompletionList
                  categories={data.categories}
                  getCategoryByCode={getCategoryByCode}
                  language={language}
                  categoryEmissions={summary?.category_emissions}
                  formatChartValue={formatChartValue}
                  onToggle={toggleCategoryCompleted}
                  togglingCode={togglingCode}
                />
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Gamification */}
      {summary?.gamification && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className={`p-6 rounded-2xl ${isDark ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30' : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'}`}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-amber-500/30">
              <Award className="w-8 h-8 text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {summary.gamification.message || t('dashboard.gamification.defaultMsg')}
              </h3>
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                {summary.gamification.next_milestone || t('dashboard.gamification.defaultNext')}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-amber-500">
                {stats.totalCategories > 0
                  ? Math.round((stats.completedCategories / stats.totalCategories) * 100)
                  : 0}%
              </div>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t('dashboard.gamification.completed')}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Plausibility check */}
      <PlausibilitySection
        plausibilityResult={plausibilityResult}
        plausibilityLoading={plausibilityLoading}
        runPlausibilityCheck={runPlausibilityCheck}
        dismissPlausibilityAlert={dismissPlausibilityAlert}
        restorePlausibilityAlert={restorePlausibilityAlert}
      />
    </div>
  );
};

// v1.5 (P2 LOT A) — au-delà de ce nombre d'alertes, la liste est repliée.
const PLAUSIBILITY_COLLAPSE_THRESHOLD = 3;

// L3 — appréciation de VRAISEMBLANCE PHYSIQUE : assessTile + fourchettes vivent
// dans utils/plausibilityVerdict.js (source unique partagée avec la pré-clôture).
const TILE_BLOB = {
  green:   'bg-green-500',
  orange:  'bg-amber-500',
  red:     'bg-red-500',
  neutral: 'bg-gray-300',
};
const TILE_ASSESS_LABEL = {
  green:  'assessGreen',
  orange: 'assessOrange',
  red:    'assessRed',
};

// L2a/L3 P3 — tuiles KPI physiques. Style aligné sur les cartes stats du header
// (icône ronde colorée + label + valeur), PAS le composant KPICards (couplé au
// contrat kpis dashboard). Blob d'état = vraisemblance (L3), gris si ratio null.
const PhysicalKpiTiles = ({ kpis, isDark, t }) => {
  if (!kpis) return null;
  const defs = [
    { key: 'elec', block: kpis.elec, ratioKey: 'kwh_m2', icon: Zap, color: 'amber',
      label: t('dashboard.plausibility.tiles.elec'), unit: t('dashboard.plausibility.tiles.kwhM2'),
      noDenom: t('dashboard.plausibility.tiles.noSurface') },
    { key: 'heat', block: kpis.heat, ratioKey: 'kwh_m2', icon: Flame, color: 'orange',
      label: t('dashboard.plausibility.tiles.heat'), unit: t('dashboard.plausibility.tiles.kwhM2'),
      noDenom: t('dashboard.plausibility.tiles.noSurface') },
    { key: 'commute', block: kpis.commute, ratioKey: 'km_fte', icon: Car, color: 'sky',
      label: t('dashboard.plausibility.tiles.commute'), unit: t('dashboard.plausibility.tiles.kmFte'),
      noDenom: t('dashboard.plausibility.tiles.noFte') },
  ].filter(d => d.block); // bloc null = catégorie vide = pas de tuile
  if (defs.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4" data-testid="plausibility-tiles">
      {defs.map(({ key, block, ratioKey, icon: Icon, color, label, unit, noDenom }) => {
        const ratio = block[ratioKey];
        const state = assessTile(key, ratio);
        const assessLabel = TILE_ASSESS_LABEL[state];
        return (
          <div
            key={key}
            data-testid={`plausibility-tile-${key}`}
            data-assessment={state}
            className={`p-5 rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-white shadow-sm border border-gray-100'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`relative p-3 rounded-xl bg-${color}-500/20`}>
                <Icon className={`w-6 h-6 text-${color}-500`} />
                {/* Blob d'état de vraisemblance (L3), gris si ratio null */}
                <span
                  data-testid={`plausibility-tile-blob-${key}`}
                  className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ring-2 ${isDark ? 'ring-slate-800' : 'ring-white'} ${TILE_BLOB[state]}`}
                />
              </div>
              <div className="min-w-0">
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{label}</p>
                {ratio !== null && ratio !== undefined ? (
                  <p className="text-2xl font-bold">
                    {ratio.toLocaleString('fr-CH', { maximumFractionDigits: 1 })}
                    <span className={`text-sm font-normal ml-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{unit}</span>
                  </p>
                ) : (
                  <p className={`text-sm mt-0.5 italic ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{noDenom}</p>
                )}
              </div>
            </div>
            {assessLabel && (
              <p className={`text-xs mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {t(`dashboard.plausibility.tiles.${assessLabel}`)}
              </p>
            )}
            {block.n_excluded > 0 && (
              <p className={`text-[11px] mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                {t('dashboard.plausibility.tiles.excluded').replace('{count}', block.n_excluded)}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const PlausibilitySection = ({ plausibilityResult, plausibilityLoading, runPlausibilityCheck, dismissPlausibilityAlert, restorePlausibilityAlert }) => {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const { isCsAdmin, isCompanyAdmin } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [dismissedOpen, setDismissedOpen] = useState(false);
  const [remarksOpen, setRemarksOpen] = useState(false);
  const [verdictOpen, setVerdictOpen] = useState(false);
  // Retrigger du pulse verdict (cf. effet plus bas) : compteur remonté en `key`.
  const [pulseKey, setPulseKey] = useState(0);
  const prevAlertLevelRef = useRef(null);

  // L2b — l'UI dismiss n'est visible que pour les rôles qui passeront la garde
  // BE require_fy_modify_state (cs_admin OU admin) — même logique FE que les
  // actions close/rectify (useFiscalYearsPage : isAdmin = isCsAdmin || isCompanyAdmin).
  const canDismiss = (isCsAdmin || isCompanyAdmin) && !!dismissPlausibilityAlert;

  // v1.5 — AUTO-RUN à l'ouverture de l'onglet (TrackingTab est monté
  // conditionnellement, cf. Dashboard.js:69) + re-run au changement de FY
  // (runPlausibilityCheck est un useCallback keyé sur currentFiscalYear.id).
  // Garde anti-double-fetch : dédup par identité du callback → 1 seul POST
  // par (ouverture d'onglet | changement de FY), StrictMode inclus.
  const lastRunRef = useRef(null);
  useEffect(() => {
    if (lastRunRef.current === runPlausibilityCheck) return;
    lastRunRef.current = runPlausibilityCheck;
    runPlausibilityCheck();
  }, [runPlausibilityCheck]);

  const summary = plausibilityResult?.summary;
  // Verdict 3 états COMPOSÉ (règles BE + tuiles FE) — source unique partagée.
  // Formulation PRUDENTE : jamais « validé ».
  const { level: verdict, rulesLevel, tileLevel, redTiles, orangeTiles } =
    composeVerdict(summary, plausibilityResult?.physical_kpis);

  const VERDICT_DOT = { ok: 'bg-green-500', warning: 'bg-amber-500', critical: 'bg-red-500' };
  const VERDICT_TEXT = {
    ok: isDark ? 'text-green-300' : 'text-green-700',
    warning: isDark ? 'text-amber-300' : 'text-amber-700',
    critical: isDark ? 'text-red-300' : 'text-red-700',
  };
  // Libellé selon la SOURCE (règles / tuiles / mixte). Les tuiles au niveau
  // composé sont nommées via leurs labels i18n.
  const tilesAtLevel = verdict === 'critical' ? redTiles : verdict === 'warning' ? orangeTiles : [];
  const tileLabels = tilesAtLevel.map(k => t(`dashboard.plausibility.tiles.${k}`)).join(', ');
  const rulesAtLevel = rulesLevel === verdict;
  const tilesContribute = tileLevel === verdict && tilesAtLevel.length > 0;

  let baseLabel;
  if (verdict === 'ok') {
    baseLabel = t('dashboard.plausibility.verdictOk');
  } else if (rulesAtLevel) {
    // Règles seules OU mixte : libellé règles (le plus grave) + suffixe tuiles.
    baseLabel = verdict === 'critical'
      ? t('dashboard.plausibility.verdictCritical')
      : t('dashboard.plausibility.verdictWarning').replace('{count}', summary?.warning ?? 0);
    if (tilesContribute) {
      baseLabel += ' ' + t('dashboard.plausibility.verdictTilesSuffix').replace('{labels}', tileLabels);
    }
  } else {
    // Tuiles seules en cause.
    baseLabel = (verdict === 'critical'
      ? t('dashboard.plausibility.verdictTilesRed')
      : t('dashboard.plausibility.verdictTilesOrange')).replace('{labels}', tileLabels);
  }

  // L2b — jamais un 🟢 pur obtenu en masquant : quand des contrôles sont
  // ignorés, le verdict porte le suffixe « (N ignoré(s)) » sur les 3 états.
  const dismissedCount = summary?.dismissed ?? 0;
  const dismissedSuffix = dismissedCount > 0
    ? ' ' + t('dashboard.plausibility.verdictDismissedSuffix').replace('{count}', dismissedCount)
    : '';
  const verdictLabel = baseLabel + dismissedSuffix;

  const alerts = plausibilityResult?.alerts || [];
  const dismissed = plausibilityResult?.dismissed || [];
  const shownAlerts = expanded ? alerts : alerts.slice(0, PLAUSIBILITY_COLLAPSE_THRESHOLD);
  const hiddenCount = alerts.length - PLAUSIBILITY_COLLAPSE_THRESHOLD;

  // Remarques (ℹ️) — depuis le downgrade dominance→info (L3), les alertes info
  // ACTIVES sont invisibles sous un verdict vert (liste masquée). On les rend
  // consultables SANS dégrader le verdict : ligne discrète repliable, uniquement
  // quand verdict vert (sinon elles sont déjà dans la liste 🟡/🚨). Les info
  // ignorées restent dans « ignorés » (elles ne sont pas dans `alerts`).
  const infoAlerts = alerts.filter(a => a.severity === 'info');
  const showRemarksLine = verdict === 'ok' && infoAlerts.length > 0;

  // Alertes critical/warning : la ligne verdict porte SEULE le message, la liste
  // est repliée par défaut (généralisation du pattern « ligne remarques »). La
  // pastille pulse à l'apparition sous 🟡/🚨 (jamais sous 🟢). Le chevron n'apparaît
  // que s'il y a une liste à déplier (alertes actives > 0) : un 🟡/🚨 porté par une
  // tuile seule (0 alerte règle) pulse mais n'a pas de chevron.
  const verdictIsAlert = verdict === 'warning' || verdict === 'critical';
  const hasActiveAlerts = verdictIsAlert && alerts.length > 0;

  // Pulse « à l'apparition » sur un composant PERSISTANT : le montage ne suffit
  // pas — le check finit APRÈS le montage, ou l'onglet est déjà monté à l'arrivée
  // de l'utilisateur → l'animation a joué (invisible) puis s'est figée. Retrigger
  // EXPLICITE : on incrémente pulseKey à chaque transition vers un verdict alerte
  // (null/🟢 → 🟡/🚨) ou changement de niveau (🟡 ↔ 🚨). Porté en `key` sur la
  // pastille, ce compteur REMONTE le nœud → l'animation CSS `.remark-badge-pulse`
  // rejoue de façon fiable (×3 puis stable). Jamais sur 🟢 (alertLevel = null).
  useEffect(() => {
    const alertLevel = verdictIsAlert ? verdict : null;
    if (alertLevel && alertLevel !== prevAlertLevelRef.current) {
      setPulseKey(k => k + 1);
    }
    prevAlertLevelRef.current = alertLevel;
  }, [verdict, verdictIsAlert]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      data-testid="plausibility-section"
      className={`p-6 rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-white shadow-sm border border-gray-100'}`}
    >
      {/* Header minimal — plus de bouton (auto-run v1.5) */}
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-xl bg-sky-500/20">
          <ShieldCheck className="w-6 h-6 text-sky-500" />
        </div>
        <div>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('dashboard.plausibility.title')}
          </h2>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {t('dashboard.plausibility.subtitle')}
          </p>
        </div>
      </div>

      {plausibilityLoading && (
        <div data-testid="plausibility-loading" className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          {t('dashboard.plausibility.running')}
        </div>
      )}

      {/* L2a P3 — tuiles KPI physiques. Une tuile par bloc NON null (catégorie
          vide = pas de tuile, par construction BE). Rendu NEUTRE : aucun
          seuil/couleur (calibration = L3). */}
      {!plausibilityLoading && plausibilityResult && (
        <PhysicalKpiTiles kpis={plausibilityResult.physical_kpis} isDark={isDark} t={t} />
      )}

      <AnimatePresence>
        {!plausibilityLoading && plausibilityResult && verdict && (
          <motion.div
            key={verdict}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            data-testid={`plausibility-verdict-${verdict}`}
          >
            {/* Verdict global 3 états — devient le TOGGLE des alertes quand il en
                existe d'actives (repliées par défaut). Pastille : pulse à
                l'apparition sous 🟡/🚨 (jamais sous 🟢). */}
            {hasActiveAlerts ? (
              <button
                type="button"
                data-testid="plausibility-verdict-toggle"
                onClick={() => setVerdictOpen(o => !o)}
                className="w-full flex items-center gap-2.5 text-left"
              >
                <span
                  key={pulseKey}
                  data-testid="plausibility-verdict-dot"
                  data-pulse-key={pulseKey}
                  className={`remark-badge-pulse w-2.5 h-2.5 rounded-full flex-shrink-0 ${VERDICT_DOT[verdict]}`}
                />
                <span className={`text-sm font-medium ${VERDICT_TEXT[verdict]}`}>{verdictLabel}</span>
                <span className={`ml-auto text-xs flex-shrink-0 ${VERDICT_TEXT[verdict]}`} aria-hidden="true">
                  {verdictOpen ? '▾' : '▸'}
                </span>
              </button>
            ) : (
              <div className="flex items-center gap-2.5">
                <span
                  key={pulseKey}
                  data-testid="plausibility-verdict-dot"
                  data-pulse-key={pulseKey}
                  className={`${verdictIsAlert ? 'remark-badge-pulse ' : ''}w-2.5 h-2.5 rounded-full flex-shrink-0 ${VERDICT_DOT[verdict]}`}
                />
                <span className={`text-sm font-medium ${VERDICT_TEXT[verdict]}`}>{verdictLabel}</span>
              </div>
            )}

            {/* Cas 🟢 : une phrase rassurante, RIEN d'autre (le silence par défaut) */}
            {verdict === 'ok' && (
              <p className={`text-xs mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {t('dashboard.plausibility.verdictOkSub')}
              </p>
            )}

            {/* Ligne remarques (ℹ️) — consultable sans dégrader le 🟢. Même pattern
                repli/dépli que « N ignoré(s) ». Repliée par défaut. Le badge ℹ️
                pulse 3× à l'apparition (CSS finie), puis stable. */}
            {showRemarksLine && (
              <div className="mt-2" data-testid="plausibility-remarks-section">
                <button
                  data-testid="plausibility-remarks-toggle"
                  onClick={() => setRemarksOpen(o => !o)}
                  className={`flex items-center gap-1.5 text-xs font-medium ${isDark ? 'text-sky-400 hover:text-sky-300' : 'text-sky-600 hover:text-sky-700'}`}
                >
                  <span
                    data-testid="plausibility-remarks-badge"
                    className="remark-badge-pulse inline-flex"
                    aria-hidden="true"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </span>
                  {t('dashboard.plausibility.remarksCount').replace('{count}', infoAlerts.length)}
                  {remarksOpen ? ' ▾' : ' ▸'}
                </button>
                {remarksOpen && (
                  <div className="space-y-2 mt-2" data-testid="plausibility-remarks-list">
                    {infoAlerts.map((alert, idx) => (
                      <div
                        key={`remark-${idx}`}
                        className={`flex items-start gap-3 p-3 rounded-xl border ${isDark ? 'bg-sky-500/10 border-sky-500/30' : 'bg-sky-50 border-sky-200'}`}
                      >
                        <Info className="w-4 h-4 text-sky-500 flex-shrink-0" />
                        <span className={`text-sm flex-1 ${isDark ? 'text-sky-300' : 'text-sky-800'}`}>{alert.message}</span>
                        {canDismiss && (
                          <button
                            data-testid={`plausibility-dismiss-${alert.rule}`}
                            title={t('dashboard.plausibility.dismissTitle')}
                            onClick={() => dismissPlausibilityAlert(alert.rule)}
                            className={`p-1 rounded-lg flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity ${isDark ? 'hover:bg-slate-700' : 'hover:bg-white/70'}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Cas 🟡 / 🚨 : liste DÉPLIÉE via le toggle verdict (repliée par défaut).
                Tri sévérité + dismiss ✕ + « Voir N de plus » (>3) conservés. */}
            {hasActiveAlerts && verdictOpen && (
              <>
                <div className="space-y-2 mt-3">
                  {shownAlerts.map((alert, idx) => {
                    const severityConfig = {
                      critical: {
                        bg: isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200',
                        icon: <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />,
                        text: isDark ? 'text-red-300' : 'text-red-800',
                      },
                      warning: {
                        bg: isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200',
                        icon: <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />,
                        text: isDark ? 'text-amber-300' : 'text-amber-800',
                      },
                      info: {
                        bg: isDark ? 'bg-sky-500/10 border-sky-500/30' : 'bg-sky-50 border-sky-200',
                        icon: <Info className="w-4 h-4 text-sky-500 flex-shrink-0" />,
                        text: isDark ? 'text-sky-300' : 'text-sky-800',
                      },
                    };
                    const cfg = severityConfig[alert.severity] || severityConfig.info;
                    return (
                      <div
                        key={`alert-${idx}`}
                        className={`flex items-start gap-3 p-3 rounded-xl border ${cfg.bg}`}
                      >
                        {cfg.icon}
                        <span className={`text-sm flex-1 ${cfg.text}`}>{alert.message}</span>
                        {canDismiss && (
                          <button
                            data-testid={`plausibility-dismiss-${alert.rule}`}
                            title={t('dashboard.plausibility.dismissTitle')}
                            onClick={() => dismissPlausibilityAlert(alert.rule)}
                            className={`p-1 rounded-lg flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity ${isDark ? 'hover:bg-slate-700' : 'hover:bg-white/70'}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {alerts.length > PLAUSIBILITY_COLLAPSE_THRESHOLD && (
                  <button
                    data-testid="plausibility-toggle"
                    onClick={() => setExpanded(e => !e)}
                    className={`text-xs mt-2 font-medium ${isDark ? 'text-sky-400 hover:text-sky-300' : 'text-sky-600 hover:text-sky-700'}`}
                  >
                    {expanded
                      ? t('dashboard.plausibility.showLess')
                      : t('dashboard.plausibility.showMore').replace('{count}', hiddenCount)}
                  </button>
                )}
                <p className={`text-xs mt-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {t('dashboard.plausibility.contextExercise')} : {plausibilityResult.context_used.fiscal_year || '—'}
                  {' · '}{plausibilityResult.context_used.activities_count} {t('dashboard.plausibility.contextActivities')}
                  {' · '}{t('dashboard.plausibility.contextSector')} : {plausibilityResult.context_used.sector}
                </p>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* L2b — contrôles ignorés : section repliée en bas, restaurable.
          Visible quel que soit le verdict (jamais un 🟢 obtenu en masquant
          sans trace : le suffixe verdict + cette section gardent le compte). */}
      {!plausibilityLoading && dismissed.length > 0 && (
        <div className="mt-4" data-testid="plausibility-dismissed-section">
          <button
            data-testid="plausibility-dismissed-toggle"
            onClick={() => setDismissedOpen(o => !o)}
            className={`text-xs font-medium ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {t('dashboard.plausibility.dismissedCount').replace('{count}', dismissed.length)}
            {dismissedOpen ? ' ▾' : ' ▸'}
          </button>
          {dismissedOpen && (
            <div className="space-y-2 mt-2">
              {dismissed.map((alert, idx) => (
                <div
                  key={`dismissed-${idx}`}
                  className={`flex items-start gap-3 p-3 rounded-xl border opacity-60 ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200'}`}
                >
                  <span className={`text-sm flex-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{alert.message}</span>
                  {canDismiss && (
                    <button
                      data-testid={`plausibility-restore-${alert.rule}`}
                      onClick={() => restorePlausibilityAlert(alert.rule)}
                      className={`flex items-center gap-1 text-xs font-medium flex-shrink-0 ${isDark ? 'text-sky-400 hover:text-sky-300' : 'text-sky-600 hover:text-sky-700'}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                      {t('dashboard.plausibility.restore')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default TrackingTab;
